import { useInfiniteQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { MyContext } from "../../../context/MyContext";
import { backIncstance } from "../../../utils/commonFunctions";
import { GradeType } from "../../../components/card/result/Grade";

/**
 * useInfiniteSocratesResultsQuery
 *
 * 소크라테스 모의 면접 결과 무한 스크롤 Query Hook
 *
 * 기능:
 * - 페이지네이션 자동 관리
 * - 스크롤 시 추가 데이터 로드
 * - 캐싱 및 중복 방지
 */

const JOBSHOPPER_RESULTS_URL = "/api/v1/mypage/socra/jobshopper/results";

export interface SocratesResultItem {
  id: number;
  title: string;
  interviewType: string;
  grade: GradeType;
  interviewDate: string;
}

interface SocratesResultsPage {
  results: SocratesResultItem[];
  nextPage: number;
  hasMore: boolean;
  totalCount: number;
}

interface UseInfiniteSocratesResultsQueryOptions {
  limit?: number;
}

export const useInfiniteSocratesResultsQuery = (
  options?: UseInfiniteSocratesResultsQueryOptions
) => {
  const { limit = 8 } = options || {};
  const { user } = useContext(MyContext);
  const userId = user?.id;

  return useInfiniteQuery<SocratesResultsPage>({
    queryKey: ["socratesResults", "infinite", userId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!userId) {
        return { results: [], nextPage: 1, hasMore: false, totalCount: 0 };
      }

      const response = await backIncstance(
        "GET",
        JOBSHOPPER_RESULTS_URL,
        `?offset=${pageParam}&limit=${limit}`
      );

      if (response.status === 401) {
        throw new Error("Unauthorized");
      }

      const data = await response.json();

      // 데이터 변환
      const results: SocratesResultItem[] = (data.result_list || []).map(
        (item: any) => {
          // 종합 평가 매핑
          let grade: GradeType = "satisfactory";
          if (item.total_evaluation === "우수") {
            grade = "excellent";
          } else if (item.total_evaluation === "노력") {
            grade = "improvement";
          }

          // 날짜 포맷
          const date = new Date(item.created_at);
          const interviewDate = `${date.getFullYear()}.${String(
            date.getMonth() + 1
          ).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;

          return {
            id: item.jobshopper_interview_id,
            title: item.title,
            interviewType: item.interview_type,
            grade,
            interviewDate,
          };
        }
      );

      const totalCount = data.total_count || 0;
      const hasMore = (pageParam as number) * limit < totalCount;

      return {
        results,
        nextPage: (pageParam as number) + 1,
        hasMore,
        totalCount,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextPage : undefined,
    initialPageParam: 1,
    staleTime: 30000, // 30초 동안 fresh 상태 유지
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    enabled: !!userId,
  });
};
